import apiDal from "../config/apiDal";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";

export async function compressImageToBlob(file: File, maxWidth = 1024, quality = 0.8): Promise<Blob> {
	const supportedTypes = ["image/jpeg", "image/png"];
	if (!supportedTypes.includes(file.type)) {
		throw new Error(`Unsupported file type: ${file.type}. Only JPEG and PNG are supported.`);
	}

	console.log(`Original file size: ${(file.size / 1024).toFixed(2)} KB`);

	const imageBitmap = await createImageBitmap(file);

	const canvas = document.createElement("canvas");
	const scale = Math.min(maxWidth / imageBitmap.width, 1);
	canvas.width = imageBitmap.width * scale;
	canvas.height = imageBitmap.height * scale;

	const ctx = canvas.getContext("2d");
	if (!ctx) throw new Error("Could not get canvas context");

	ctx.drawImage(imageBitmap, 0, 0, canvas.width, canvas.height);

	return new Promise((resolve) => {
		canvas.toBlob(
			(blob) => {
				if (!blob) throw new Error("Compression failed.");
				console.log(`Compressed blob size: ${(blob.size / 1024).toFixed(2)} KB`);
				resolve(blob);
			},
			"image/jpeg", // always compress to JPEG for smaller size
			quality,
		);
	});
}

const getPresignedUrl = async (imageName: string, portalId: number, contentType: string): Promise<string> => {
	const payload = {
		key: `images/${portalId}/${imageName}`,
		operation: "putObject",
		contentType: contentType,
		public: true,
	};

	try {
		const response = await apiDal.post<string>("/s3/getsignedurl", payload, {
			headers: {
				"Content-Type": "application/json",
			},
		});

		return response.data;
	} catch (error: any) {
		console.error("Failed to get presigned URL:", error);
		throw new Error("Could not get presigned URL");
	}
};

const getPublicPresignedUrl = async (
	imageName: string,
	enterpriseUid: string,
	contentType: string,
): Promise<string> => {
	try {
		const response = await apiDal.post<string>(
			"/s3/getpublicsignedurl",
			{ imageName: imageName, enterpriseUid: enterpriseUid, contentType: contentType, operation: "putObject" },
			{
				headers: {
					"Content-Type": "application/json",
				},
			},
		);

		return response.data as string;
	} catch (error: any) {
		console.error("Failed to get presigned URL:", error);
		throw new Error("Could not get presigned URL");
	}
};

export const uploadToPresignedUrl = async (
	file: File,
	portalId: number | string,
	isPublicUpload = false,
): Promise<string> => {
	try {
		const guid = uuidv4();

		const contentType = file.type || "application/octet-stream";

		const compressedBlob = await compressImageToBlob(file);

		const fileExtension = file.name.split(".").pop();

		const fileNameWithGuid = `${guid}.${fileExtension}`;

		let url: string;

		if (isPublicUpload) {
			url = await getPublicPresignedUrl(fileNameWithGuid, portalId.toString(), contentType);
		} else {
			url = await getPresignedUrl(fileNameWithGuid, Number(portalId), contentType);
		}

		await axios.put(url, compressedBlob, {
			headers: {
				"Content-Type": contentType,
			},
		});

		console.log("Image uploaded successfully.");

		return url.split("?")[0];
	} catch (error) {
		console.error("Image upload failed:", error);
		throw error;
	}
};
